/**
 * Big Two AI - Strategic card play
 * Plays lowest valid cards and saves power cards for later
 */

type Suit = '♠' | '♥' | '♣' | '♦'
type Rank = '3' | '4' | '5' | '6' | '7' | '8' | '9' | '10' | 'J' | 'Q' | 'K' | 'A' | '2'

interface Card {
  suit: Suit
  rank: Rank
  id: string
}

const RANK_ORDER: Rank[] = ['3', '4', '5', '6', '7', '8', '9', '10', 'J', 'Q', 'K', 'A', '2']
const SUIT_ORDER: Suit[] = ['♦', '♣', '♥', '♠']

/**
 * Get card strength for comparison
 */
function getCardStrength(card: Card): number {
  const rankValue = RANK_ORDER.indexOf(card.rank) * 10
  const suitValue = SUIT_ORDER.indexOf(card.suit)
  return rankValue + suitValue
}

/**
 * Compare cards
 */
function compareCards(a: Card, b: Card): number {
  return getCardStrength(a) - getCardStrength(b)
}

/**
 * Can beat the last play?
 */
function canBeat(cards: Card[], lastPlay: Card[]): boolean {
  if (lastPlay.length === 0) return true // First play
  if (cards.length !== lastPlay.length) return false

  const cardsSorted = [...cards].sort(compareCards)
  const lastSorted = [...lastPlay].sort(compareCards)

  const highestCard = cardsSorted[cardsSorted.length - 1]
  const lastHighest = lastSorted[lastSorted.length - 1]

  return getCardStrength(highestCard) > getCardStrength(lastHighest)
}

export class BigTwoAI {
  private hand: Card[]
  private lastPlay: Card[]
  private opponentPassCount: number

  constructor() {
    this.hand = []
    this.lastPlay = []
    this.opponentPassCount = 0
  }

  /**
   * Set AI's current hand
   */
  setHand(cards: Card[]): void {
    this.hand = [...cards].sort(compareCards)
  }

  /**
   * Update last play on table
   */
  setLastPlay(cards: Card[]): void {
    this.lastPlay = cards
  }

  /**
   * Record opponent pass
   */
  recordOpponentPass(): void {
    this.opponentPassCount++
  }

  /**
   * Reset pass count (when we win a round)
   */
  resetPassCount(): void {
    this.opponentPassCount = 0
  }

  /**
   * Find lowest valid play that beats lastPlay
   */
  private findLowestValidPlay(): Card[] | null {
    if (this.lastPlay.length === 0) {
      // First play - must play 3 of diamonds if we have it
      const threeOfDiamonds = this.hand.find(c => c.rank === '3' && c.suit === '♦')
      if (threeOfDiamonds) {
        return [threeOfDiamonds]
      }
      // Otherwise play lowest single
      return [this.hand[0]]
    }

    const playSize = this.lastPlay.length

    // Try to find valid plays of same size
    if (playSize === 1) {
      // Single card
      for (const card of this.hand) {
        if (canBeat([card], this.lastPlay)) {
          return [card]
        }
      }
    } else if (playSize === 2) {
      // Pair
      const pairs = this.findPairs()
      for (const pair of pairs) {
        if (canBeat(pair, this.lastPlay)) {
          return pair
        }
      }
    } else if (playSize === 3) {
      // Triple
      const triples = this.findTriples()
      for (const triple of triples) {
        if (canBeat(triple, this.lastPlay)) {
          return triple
        }
      }
    } else if (playSize === 5) {
      // 5-card combos: try in order of power
      // Straight Flush > Four of a Kind > Full House > Flush > Straight
      const straightFlushes = this.findStraightFlushes()
      for (const sf of straightFlushes) {
        if (canBeat(sf, this.lastPlay)) return sf
      }

      const fours = this.findFourOfAKind()
      for (const four of fours) {
        if (canBeat(four, this.lastPlay)) return four
      }

      const fullHouses = this.findFullHouses()
      for (const fh of fullHouses) {
        if (canBeat(fh, this.lastPlay)) return fh
      }

      const flushes = this.findFlushes()
      for (const flush of flushes) {
        if (canBeat(flush, this.lastPlay)) return flush
      }

      const straights = this.findStraights()
      for (const straight of straights) {
        if (canBeat(straight, this.lastPlay)) return straight
      }
    }

    return null // Can't beat
  }

  /**
   * Find all pairs in hand
   */
  private findPairs(): Card[][] {
    const pairs: Card[][] = []
    const rankGroups = new Map<Rank, Card[]>()

    for (const card of this.hand) {
      const group = rankGroups.get(card.rank) || []
      group.push(card)
      rankGroups.set(card.rank, group)
    }

    for (const group of rankGroups.values()) {
      if (group.length >= 2) {
        // Use two lowest suits
        const sorted = group.sort(compareCards)
        pairs.push([sorted[0], sorted[1]])
      }
    }

    return pairs
  }

  /**
   * Find all triples in hand
   */
  private findTriples(): Card[][] {
    const triples: Card[][] = []
    const rankGroups = new Map<Rank, Card[]>()

    for (const card of this.hand) {
      const group = rankGroups.get(card.rank) || []
      group.push(card)
      rankGroups.set(card.rank, group)
    }

    for (const group of rankGroups.values()) {
      if (group.length >= 3) {
        const sorted = group.sort(compareCards)
        triples.push([sorted[0], sorted[1], sorted[2]])
      }
    }

    return triples
  }

  /**
   * Find all straights (5 consecutive cards)
   */
  private findStraights(): Card[][] {
    const straights: Card[][] = []
    if (this.hand.length < 5) return straights

    // Group cards by rank
    const rankGroups = new Map<Rank, Card[]>()
    for (const card of this.hand) {
      const group = rankGroups.get(card.rank) || []
      group.push(card)
      rankGroups.set(card.rank, group)
    }

    // Try to build straights starting from each rank
    for (let startIdx = 0; startIdx <= RANK_ORDER.length - 5; startIdx++) {
      const neededRanks = RANK_ORDER.slice(startIdx, startIdx + 5)

      // Check if we have all 5 consecutive ranks
      const hasAll = neededRanks.every(rank => rankGroups.has(rank))
      if (!hasAll) continue

      // Build straight using lowest suit of each rank
      const straight: Card[] = []
      for (const rank of neededRanks) {
        const cards = rankGroups.get(rank)!.sort(compareCards)
        straight.push(cards[0]) // Use lowest suit
      }

      straights.push(straight)
    }

    return straights
  }

  /**
   * Find all flushes (5 cards of same suit)
   */
  private findFlushes(): Card[][] {
    const flushes: Card[][] = []
    if (this.hand.length < 5) return flushes

    // Group by suit
    const suitGroups = new Map<Suit, Card[]>()
    for (const card of this.hand) {
      const group = suitGroups.get(card.suit) || []
      group.push(card)
      suitGroups.set(card.suit, group)
    }

    // Find flushes (suits with 5+ cards)
    for (const group of suitGroups.values()) {
      if (group.length >= 5) {
        // Use lowest 5 cards of this suit
        const sorted = group.sort(compareCards)
        flushes.push(sorted.slice(0, 5))
      }
    }

    return flushes
  }

  /**
   * Find all full houses (triple + pair)
   */
  private findFullHouses(): Card[][] {
    const fullHouses: Card[][] = []

    // Group by rank
    const rankGroups = new Map<Rank, Card[]>()
    for (const card of this.hand) {
      const group = rankGroups.get(card.rank) || []
      group.push(card)
      rankGroups.set(card.rank, group)
    }

    const triples: Card[][] = []
    const pairs: Card[][] = []

    for (const group of rankGroups.values()) {
      if (group.length >= 3) {
        const sorted = group.sort(compareCards)
        triples.push([sorted[0], sorted[1], sorted[2]])
        // If 4 of a kind, can also use 3 as triple
        if (group.length === 4) {
          triples.push([sorted[1], sorted[2], sorted[3]])
        }
      }
      if (group.length >= 2) {
        const sorted = group.sort(compareCards)
        pairs.push([sorted[0], sorted[1]])
        // If 3+ cards, can make multiple pairs
        if (group.length >= 3) {
          pairs.push([sorted[1], sorted[2]])
        }
        if (group.length === 4) {
          pairs.push([sorted[2], sorted[3]])
        }
      }
    }

    // Combine triples with pairs from different ranks
    for (const triple of triples) {
      for (const pair of pairs) {
        if (triple[0].rank !== pair[0].rank) {
          fullHouses.push([...triple, ...pair])
        }
      }
    }

    return fullHouses
  }

  /**
   * Find all four of a kinds (4 cards + 1 kicker)
   */
  private findFourOfAKind(): Card[][] {
    const fours: Card[][] = []

    // Group by rank
    const rankGroups = new Map<Rank, Card[]>()
    for (const card of this.hand) {
      const group = rankGroups.get(card.rank) || []
      group.push(card)
      rankGroups.set(card.rank, group)
    }

    // Find four of a kind
    for (const [rank, group] of rankGroups.entries()) {
      if (group.length === 4) {
        // Find a kicker from different rank
        for (const [kickerRank, kickerGroup] of rankGroups.entries()) {
          if (kickerRank !== rank) {
            const sorted = kickerGroup.sort(compareCards)
            fours.push([...group, sorted[0]]) // Use lowest kicker
            break // Only need one kicker
          }
        }
      }
    }

    return fours
  }

  /**
   * Find all straight flushes (5 consecutive cards of same suit)
   */
  private findStraightFlushes(): Card[][] {
    const straightFlushes: Card[][] = []
    if (this.hand.length < 5) return straightFlushes

    // Group by suit first
    const suitGroups = new Map<Suit, Card[]>()
    for (const card of this.hand) {
      const group = suitGroups.get(card.suit) || []
      group.push(card)
      suitGroups.set(card.suit, group)
    }

    // For each suit with 5+ cards, check for straights
    for (const suitCards of suitGroups.values()) {
      if (suitCards.length < 5) continue

      // Group by rank within this suit
      const rankMap = new Map<Rank, Card>()
      for (const card of suitCards) {
        rankMap.set(card.rank, card)
      }

      // Try to build straights
      for (let startIdx = 0; startIdx <= RANK_ORDER.length - 5; startIdx++) {
        const neededRanks = RANK_ORDER.slice(startIdx, startIdx + 5)

        // Check if we have all 5 consecutive ranks in this suit
        const hasAll = neededRanks.every(rank => rankMap.has(rank))
        if (!hasAll) continue

        // Build straight flush
        const straightFlush: Card[] = []
        for (const rank of neededRanks) {
          straightFlush.push(rankMap.get(rank)!)
        }

        straightFlushes.push(straightFlush)
      }
    }

    return straightFlushes
  }

  /**
   * Decide what to play
   */
  play(): Card[] | 'pass' {
    if (this.hand.length === 0) return 'pass'

    // Strategy: Play lowest valid cards, save 2s and Aces
    const lowestPlay = this.findLowestValidPlay()

    if (!lowestPlay) {
      // Can't beat - pass
      this.opponentPassCount = 0 // Reset opponent pass count
      return 'pass'
    }

    // Check if play contains power cards (2, A, K)
    const hasPowerCards = lowestPlay.some(c => ['2', 'A', 'K'].includes(c.rank))

    // If opponent has passed multiple times and we have power cards, save them
    if (hasPowerCards && this.opponentPassCount < 2 && this.hand.length > 3) {
      return 'pass'
    }

    return lowestPlay
  }

  /**
   * Remove played cards from hand
   */
  removeCards(cards: Card[]): void {
    const cardIds = new Set(cards.map(c => c.id))
    this.hand = this.hand.filter(c => !cardIds.has(c.id))
  }

  /**
   * Reset AI state
   */
  reset(): void {
    this.hand = []
    this.lastPlay = []
    this.opponentPassCount = 0
  }

  /**
   * Get hand size
   */
  getHandSize(): number {
    return this.hand.length
  }
}
